import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Tag, Row, Col } from 'antd';
import '../App.css'; // Make sure to import your CSS file
import LiquidGlass from './LiquidGlass';

import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, PointElement, ScatterController } from 'chart.js';

// Draw ONE filled rectangle per step segment. Registered globally so it always runs.
const StepBarsPlugin = {
    id: 'stepBars',
    afterDatasetsDraw(chart, _args, pluginOptions) {
        const segments = pluginOptions?.segments;
        if (!Array.isArray(segments) || segments.length === 0) return;

        const { ctx, chartArea, scales } = chart;
        const xScale = scales?.x;
        const yScale = scales?.y;
        if (!xScale || !yScale || !chartArea) return;

        // Use the visual baseline (bottom of chart area) for consistent filled rectangles,
        // even if the y-scale doesn't include 0 in its domain.
        const y0 = chartArea.bottom;

        ctx.save();
        // Clip to chart area so rectangles don't overflow axes/labels.
        ctx.beginPath();
        ctx.rect(chartArea.left, chartArea.top, chartArea.right - chartArea.left, chartArea.bottom - chartArea.top);
        ctx.clip();

        segments.forEach((s) => {
            const x0 = xScale.getPixelForValue(s.start);
            const x1 = xScale.getPixelForValue(s.end);
            const y1 = yScale.getPixelForValue(s.watt);

            const left = Math.min(x0, x1);
            const width = Math.max(1, Math.abs(x1 - x0));
            const top = Math.min(y0, y1);
            const height = Math.max(1, Math.abs(y0 - y1));

            ctx.fillStyle = s.fillColor || 'rgba(0,0,0,0.12)';
            if (typeof ctx.roundRect === 'function') {
                ctx.beginPath();
                ctx.roundRect(left, top, width, height, 2);
                ctx.fill();
            } else {
                ctx.fillRect(left, top, width, height);
            }

            ctx.strokeStyle = s.strokeColor || 'rgba(0,0,0,0.30)';
            ctx.lineWidth = 0.6;
            ctx.strokeRect(left, top, width, height);
        });

        ctx.restore();
    },
};

// NOTE: We use a scatter dataset (invisible points) for hit-testing + tooltips,
// so we must register ScatterController when using modular Chart.js imports.
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, ScatterController, StepBarsPlugin, Title, Tooltip, Legend);


const TrainingChart = ({ steps, FTP, isMobile, powerUnit = "watts" }) => {
    // Check if steps array is empty

    // Create a translucent variant of an rgba() color (same helper idea as HomeTrainerProgress)
    function withAlpha(rgbaColor, alpha) {
        const match = rgbaColor.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\s*\)/i);
        if (!match) return rgbaColor;
        const r = parseInt(match[1], 10);
        const g = parseInt(match[2], 10);
        const b = parseInt(match[3], 10);
        const a = typeof alpha === 'number' ? alpha : (match[4] ? parseFloat(match[4]) : 1);
        return `rgba(${r}, ${g}, ${b}, ${a})`;
    }

    const GlassTag = ({ accent, children }) => {
        // Not too transparent: keep the tint readable/legible.
        const tintStrong = withAlpha(accent, 0.34);
        const tintSoft = withAlpha(accent, 0.16);
        const borderCol = withAlpha(accent, 0.55);
        const glow = withAlpha(accent, 0.22);
        return (
            <Tag
                style={{
                    margin: 0, // spacing is handled by Row gutter for strict consistency
                    width: "100%",
                    textAlign: "center",
                    fontWeight: 700,
                    color: "rgba(0,0,0,0.78)",
                    borderRadius: 12,
                    padding: isMobile ? "5px 8px" : "6px 10px",
                    background: `linear-gradient(180deg, ${tintStrong}, ${tintSoft})`,
                    border: `1px solid ${borderCol}`,
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    boxShadow: `0 10px 22px ${glow}, 0 10px 22px rgba(0,0,0,0.10)`,
                }}
            >
                {children}
            </Tag>
        );
    };

    // Some screens can render this chart before FTP is set.
    // Use a safe fallback so the chart still draws (colors/zones won't go NaN).
    const ftpNum = Number(FTP);
    const ftpSafe = Number.isFinite(ftpNum) && ftpNum > 0 ? ftpNum : 100;

    const pctThresholds = [60, 75, 90, 105, 119, 150];
    const zones = powerUnit === "percent"
        ? {
            Z1: `<${pctThresholds[0]}%`,
            Z2: `<${pctThresholds[1]}%`,
            Z3: `<${pctThresholds[2]}%`,
            Z4: `<${pctThresholds[3]}%`,
            Z5: `<${pctThresholds[4]}%`,
            Z6: `<${pctThresholds[5]}%`,
            Z7: `>=${pctThresholds[5]}%`,
        }
        : {
            Z1: `<60%-${Math.round(0.6 * ftpSafe)}W`,
            Z2: `<75%-${Math.round(0.75 * ftpSafe)}W`,
            Z3: `<90%-${Math.round(0.9 * ftpSafe)}W`,
            Z4: `<105%-${Math.round(1.05 * ftpSafe)}W`,
            Z5: `<119%-${Math.round(1.19 * ftpSafe)}W`,
            Z6: `<150%-${Math.round(1.5 * ftpSafe)}W`,
            Z7: `>=150%-${Math.round(1.5 * ftpSafe)}W`,
        }
    const zones2 = {
        Z1: 'Recovery',
        Z2: 'Endurance',
        Z3: 'Tempo',
        Z4: 'Threshold',
        Z5: 'VO₂ Max',
        Z6: 'Anaerobic',
        Z7: 'Ouch...',

    }
    const getZoneindexFromPct = (pct) => {
        if (pct >= 150) return 6;
        if (pct >= 119) return 5;
        if (pct >= 105) return 4;
        if (pct >= 90) return 3;
        if (pct >= 75) return 2;
        if (pct >= 60) return 1;
        return 0;
    };
    const getZoneindex = (intensity) => {
        if (intensity >= ftpSafe * 1.5) {
            return 6; // Red
        } else if (intensity >= ftpSafe * 1.19) {
            return 5; // Yellow
        } else if (intensity >= ftpSafe * 1.05) {
            return 4; // Yellow
        } else if (intensity >= ftpSafe * 0.9) {
            return 3; // Yellow
        } else if (intensity >= ftpSafe * 0.75) {
            return 2; // Yellow
        }
        else if (intensity >= ftpSafe * 0.6) {
            return 1; // blue
        } else {
            return 0;
        }
    };
    function formatTime(seconds) {
        let reverse = false
        if (seconds < 0) {
            seconds = -seconds
            reverse = true
        }
        const minutes = Math.floor(seconds / 60); // Get the full minutes
        const remainingSeconds = seconds % 60;    // Get the remaining seconds
        // Return the formatted time string
        // Add a leading zero to seconds if less than 10
        const timeStr = reverse ? `- ${minutes}'${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}` : `${minutes}'${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
        return timeStr
    }
    // Format seconds to m:ss for axis tick labels
    function formatTimeLabel(seconds) {
        if (seconds == null || isNaN(seconds)) return '';
        const totalSeconds = Math.max(0, Math.round(Number(seconds)));
        const minutes = Math.floor(totalSeconds / 60);
        const remainingSeconds = totalSeconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    }
    const getColor = (intensity) => {
        if (intensity >= ftpSafe * 1.5) {
            return 'rgba(128, 0, 128, 1)';
        } else if (intensity >= ftpSafe * 1.19) {
            return 'rgba(255, 0, 0, 1)';
        } else if (intensity >= ftpSafe * 1.05) {
            return 'rgba(255, 165, 0, 1)';
        } else if (intensity >= ftpSafe * 0.9) {
            return 'rgba(255, 215, 0, 1)';
        } else if (intensity >= ftpSafe * 0.75) {
            return 'rgba(0, 128, 0, 1)';
        }
        else if (intensity > ftpSafe * 0.6) {
            return 'rgba(153, 204, 255, 1)';
        } else {
            return 'rgba(128, 128, 128, 1)';
        }
    };

    const getDurationSec = (step) => {
        // App normalizes to `duration` (seconds), but backend plans use `duration_sec`.
        const raw =
            step?.duration ??
            step?.duration_sec ??
            step?.duration_seconds ??
            step?.durationSecs ??
            step?.durationSec;
        const n = Math.round(Number(raw) || 0);
        return Math.max(0, n);
    };

    const getWatts = (step) => {
        // Most UI paths use `watt`, but some backend/raw plan steps use `power`.
        // Also: if FTP is not set yet, App.js may compute watt=NaN; fallback to `power`.
        const candidates = [
            step?.watt,
            step?.watts,
            step?.power,
            step?.targetPower,
        ];
        for (const v of candidates) {
            const n = Number(v);
            if (Number.isFinite(n)) return n;
        }
        return 0;
    };

    const getPct = (step) => {
        const w = getWatts(step);
        return ftpSafe > 0 ? (w * 100) / ftpSafe : 0;
    };

    // IMPORTANT: duration is in seconds in the rest of the app.
    // Old version expanded to 1 bar per second (can be thousands of bars and laggy).
    // New version: draw ONE filled rectangle per step (width = step duration).
    const segments = [];
    let t = 0;
    (steps || []).forEach((step) => {
        const dur = getDurationSec(step);
        const watt = getWatts(step);
        const pct = getPct(step);
        if (dur <= 0) return;
        const start = t;
        const end = t + dur;
        const baseColor = powerUnit === "percent" ? getColor((pct / 100) * ftpSafe) : getColor(watt);
        const fillColor = withAlpha(baseColor, 0.70);
        const strokeColor = withAlpha(baseColor, 0.95);
        segments.push({
            start,
            end,
            watt: powerUnit === "percent" ? pct : watt,
            baseColor,
            fillColor,
            strokeColor,
        });
        t = end;
    });
    const totalDurationSec = t;
    const maxWatt = segments.length ? Math.max(...segments.map((s) => Number(s?.watt) || 0)) : 0;

    // Invisible scatter points (one per step) used for tooltips/hover.
    const tooltipPoints = segments.map((s, i) => ({
        x: (s.start + s.end) / 2,
        y: s.watt,
        _start: s.start,
        _end: s.end,
        _color: s.baseColor,
        _idx: i,
    }));

    const chartData = {
        labels: [],
        datasets: [{
            label: 'Watts',
            type: 'scatter',
            data: tooltipPoints,
            parsing: false,
            showLine: false,
            pointRadius: 0,
            pointHoverRadius: 0,
            pointHitRadius: 10,
            borderWidth: 0,
            backgroundColor: 'rgba(0,0,0,0)',
            borderColor: 'rgba(0,0,0,0)',
        }]
    };

    const options = {
        indexAxis: 'x',
        animation: false,
        maintainAspectRatio: false,
        scales: {
            x: {
                type: 'linear',
                min: 0,
                max: Math.max(1, totalDurationSec),
                title: {
                    display: true,
                    text: 'Duration (min:sec)'
                },
                position: 'bottom',
                stacked: true,
                ticks: {
                    callback: (value) => formatTimeLabel(value)
                }
            },
            y: {
                beginAtZero: true,
                min: 0,
                suggestedMax: powerUnit === "percent"
                    ? Math.max(120, Math.ceil((Number.isFinite(maxWatt) ? maxWatt : 0) * 1.1))
                    : Math.max(50, Math.ceil((Number.isFinite(maxWatt) ? maxWatt : 0) * 1.1)),
                title: {
                    display: true,
                    text: powerUnit === "percent" ? '% FTP' : 'Watts'
                }
            }
        },
        plugins: {
            legend: {
                display: false
            },
            title: {
                display: true,
                text: powerUnit === "percent" ? 'Training % FTP over time (min:sec)' : 'Training Watts over time (min:sec)'
            },
            // Custom plugin options
            stepBars: {
                segments,
            },
            tooltip: {
                callbacks: {
                    title: (items) => {
                        const raw = items?.[0]?.raw;
                        const a = raw?._start;
                        const b = raw?._end;
                        if (a == null || b == null) return '';
                        return `${formatTimeLabel(a)} → ${formatTimeLabel(b)}`;
                    },
                    label: (item) => {
                        const raw = item?.raw;
                        const w = raw?.y;
                        if (w == null) return '';
                        return powerUnit === "percent"
                            ? `% FTP: ${Math.round(Number(w) || 0)}%`
                            : `Watts: ${Math.round(Number(w) || 0)}W`;
                    },
                },
            },

        },
        // barThickness: "100%"
    };

    //const legendWatt = steps.map(x => x.watt)
    const legendWatt = powerUnit === "percent"
        ? [50, 70, 80, 100, 110, 120, 170] // percent values
        : [0.5 * ftpSafe, 0.7 * ftpSafe, 0.8 * ftpSafe, 1 * ftpSafe, 1.1 * ftpSafe, 1.2 * ftpSafe, 1.7 * ftpSafe]; // watts values
    const legendAccents = powerUnit === "percent"
        ? legendWatt.map((pct) => getColor((Number(pct) / 100) * ftpSafe))
        : legendWatt.map((w) => getColor(w));
    const zoneDuration = [0, 0, 0, 0, 0, 0, 0];
    (steps || []).forEach((x) => {
        zoneDuration[
            powerUnit === "percent"
                ? getZoneindexFromPct(getPct(x))
                : getZoneindex(getWatts(x))
        ] += getDurationSec(x)
    })
    return <LiquidGlass className="liquid-chart" style={{ padding: 16 }}>
        <div style={{ height: isMobile ? 260 : 360 }}>
            <Bar data={chartData} options={options} />
        </div>
        <div style={{ marginTop: "12px" }}>
            <Row gutter={[8, 8]}>
                <Col span={2}></Col>
                {legendAccents.map((accent, i) => (
                    <Col key={`zone-tag-${i}`} span={3}>
                        <GlassTag accent={accent}>
                            {steps.length > 0 ? `Z${i + 1} : ${formatTime(zoneDuration[i])}` : `Z${i + 1}`}
                        </GlassTag>
                    </Col>
                ))}
            </Row>
            <Row gutter={[8, 8]} style={{ marginTop: "8px", marginLeft: "10px" }} >
                <Col span={2}></Col>
                {legendAccents.map((_, i) => (
                    <Col key={`zone-name-${i}`} span={3}>{zones2[`Z${i + 1}`]}</Col>
                ))}
            </Row>
            <Row gutter={[8, 8]} style={{ marginTop: "4px", marginLeft: "10px" }} >
                <Col span={2}></Col>
                {legendAccents.map((_, i) => (
                    <Col key={`zone-range-${i}`} span={3}>{zones[`Z${i + 1}`]}</Col>
                ))}
            </Row>
            <Row style={{ marginTop: "12px" }}>
                <Col span={2}></Col>
                <i>More info about <a href='https://www.trainerroad.com/blog/cycling-power-zones-training-zones-explained/#:~:text=Cycling%20intensity%20levels%20are%20commonly,is%20crucial%20to%20proper%20training.' target="_blank" rel="noreferrer">cycling power zones</a></i>
            </Row>
        </div>
    </LiquidGlass>;
}

export default TrainingChart;

